import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { AvatarModule } from 'primeng/avatar';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState } from '@ngrx/signals';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-active-user-avatar',
  standalone: true,
  imports: [AvatarModule, MenuModule, Button],
  templateUrl: './active-user-avatar.component.html',
  styleUrl: './active-user-avatar.component.scss',
})
export class ActiveUserAvatarComponent implements OnInit {
  @Input() size: 'normal' | 'large' | 'xlarge' = 'normal';
  @Input() styleClass = '';
  @Input() icon = '';
  @Input() addBookmarks = false;

  readonly activeUser = inject(ActiveUserStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly globalsStore = inject(GlobalsStore);

  menuItems: MenuItem[] = [];

  constructor() {
    effect(() => {
      const bookmarkState = getState(this.bookmarkStore);

      if (
        this.addBookmarks &&
        bookmarkState.trigger &&
        bookmarkState.trigger.includes('bookmarkAdded')
      ) {
        queueMicrotask(() => {
          this.ngOnInit();
        });
      }
    });
  }
  /**
   * Initializes the component and sets up the menu items based on the state of the `addBookmarks` property.
   * If `addBookmarks` is true, it creates a structured menu including logout, private views, and unsorted views.
   * Otherwise, it creates a minimal menu with only a logout option.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    if (this.addBookmarks) {
      const privateBookmarks = this.bookmarkStore.private();
      const unsortedBookmarks = this.bookmarkStore.unsorted();

      this.menuItems = [
        this.createUserMenu(),
        this.createSeparator(),
        ...(privateBookmarks.length > 0
          ? [
              this.createViewMenu(
                'private Ansichten',
                'fas fa-eye-slash',
                privateBookmarks
              ),
            ]
          : []),
        ...(privateBookmarks.length > 0 ? [this.createSeparator()] : []),
        ...(unsortedBookmarks.length > 0
          ? [
              this.createViewMenu(
                'unsortierte Ansichten',
                'fas fa-note-sticky',
                unsortedBookmarks
              ),
            ]
          : []),
      ];
    } else {
      this.menuItems = this.createLogoutMenu();
    }
  }

  /**
   * Creates and returns a user menu configuration object.
   *
   * @return {MenuItem} The menu item object containing details about the user menu,
   * including label, icon, and associated sub-menu items.
   */
  private createUserMenu(): MenuItem {
    return {
      label: 'Nutzer:in',
      icon: 'fas fa-user-circle',
      items: this.createLogoutMenu(),
    };
  }

  private createPrivateViewsMenu(bookmarks: Bookmark[]): MenuItem[] {
    return bookmarks.length > 0
      ? [
          this.createViewMenu(
            'private Ansichten',
            'fas fa-eye-slash',
            bookmarks
          ),
        ]
      : [];
  }

  private createUnsortedViewsMenu(bookmarks: Bookmark[]): MenuItem[] {
    return bookmarks.length > 0
      ? [
          this.createViewMenu(
            'unsortierte Ansichten',
            'fas fa-note-sticky',
            bookmarks
          ),
        ]
      : [];
  }

  /**
   * Creates and returns a menu item for logging out.
   *
   * @return {MenuItem} A menu item object that includes a label, an icon, and a command
   * to trigger the logout action with a redirect.
   */
  private createLogoutMenu(): MenuItem[] {
    const menu: MenuItem[] = [
      {
        label: 'Ausloggen',
        icon: 'fa fa-right-from-bracket',
        command: () => {
          this.activeUser.logout({ redirect: true });
        },
      },
    ];

    const impersonatedUserId = this.activeUser.impersonatedBy();
    if (impersonatedUserId) {
      menu.push({
        label: 'Zurückwechseln',
        icon: 'fas fa-people-arrows',
        command: () => {
          this.activeUser.switchBack(impersonatedUserId);
        },
      });
    }
    return menu;
  }

  /**
   * Creates a menu item with the specified label, icon, and bookmarks.
   *
   * @param {string} label - The label for the menu item.
   * @param {string} icon - The icon associated with the menu item.
   * @param {Bookmark[]} bookmarks - An array of bookmarks to be included in the menu item.
   * @return {MenuItem} The created menu item containing the label, icon, and transformed bookmarks.
   */
  private createViewMenu(
    label: string,
    icon: string,
    bookmarks: Bookmark[]
  ): MenuItem {
    return {
      label,
      icon,
      items: bookmarks.map(this.getMenuItemFromBookmark),
    };
  }

  /**
   * Creates a separator menu item with optional visibility.
   *
   * @param {boolean} [visible] - Determines if the separator should be visible. Defaults to true if not provided.
   * @return {MenuItem} The menu item object representing the separator.
   */
  private createSeparator(visible?: boolean): MenuItem {
    return { separator: visible ?? true };
  }

  /**
   * Converts a Bookmark object into a MenuItem object.
   *
   * @param {Bookmark} bookmark - The bookmark object containing metadata to create a menu item.
   * @return {MenuItem} The constructed menu item object containing label and routerLink properties.
   */
  private getMenuItemFromBookmark(bookmark: Bookmark): MenuItem {
    return {
      label: bookmark.title,
      routerLink: ['ansicht', bookmark._id],
    };
  }
}
